import React, { useCallback, useState } from 'react'
import { RotatingLines } from 'react-loader-spinner'
import { ToggleInput } from 'shared/components/common/forms'
import { t, Trans } from 'src/translations'
import Image from 'shared/components/common/Image'
import {
  CAInfo,
  ToastTOSAndPrivacy
} from 'public/components/default_template/online_ordering/account/pwlessAuth/legalUtils'
import { useIsIntlRestaurant } from 'src/lib/js/hooks/useIsIntlRestaurant'
import tracker from 'src/lib/js/tracker'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'
import { AuthenticationSource } from 'public/components/default_template/online_ordering/checkout/checkoutUtils'
import { useCheckout } from 'src/public/components/online_ordering/CheckoutContext'
import { Modal, ModalOverlay, useModal } from 'shared/components/common/modal'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  LoginStage,
  PwlessAuth
} from 'public/components/default_template/online_ordering/account/pwlessAuth/PwlessAuth'
import { PasswordlessAuthenticationErrorCode } from 'src/apollo/onlineOrdering'
import { GUEST_HELP_URL } from 'shared/components/common/authentication/constants'

const CreateAccountCheckbox = () => {
  const [isLoadingPwlessLogin, setIsLoadingPwlessLogin] = useState(false)
  const [accountCreationError, setAccountCreationError] = useState<
    string | null
  >(null)
  const [accountCreationErrorCode, setAccountCreationErrorCode] = useState<
    string | null
  >(null)
  const isIntlRestaurant = useIsIntlRestaurant()
  const { createAccount, setCreateAccount } = useCheckout()
  const { customer, passwordlessLogin } = useCustomer()
  const phoneNumber = useWatch({ name: 'yourInfoPhone' })
  const email = useWatch({ name: 'yourInfoEmail' })
  const firstName = useWatch({ name: 'yourInfoFirstName' })
  const lastName = useWatch({ name: 'yourInfoLastName' })
  const {
    formState: { errors }
  } = useFormContext()
  const {
    isOpen: isAuthModalOpen,
    onClose: onCloseAuthModal,
    onOpen: onOpenAuthModal
  } = useModal()

  const sendPwlessLoginCode = useCallback(async () => {
    return await passwordlessLogin(phoneNumber)
  }, [phoneNumber, passwordlessLogin])

  const onCloseAuthModalCb = useCallback(() => {
    onCloseAuthModal()
    if (!customer) {
      setCreateAccount(false)
    }
  }, [customer, onCloseAuthModal, setCreateAccount])

  const handleCreateAccountCheckboxChange = useCallback(
    async (checked: boolean) => {
      setCreateAccount(checked)
      setAccountCreationError(null)
      setAccountCreationErrorCode(null)
      if (checked) {
        tracker.track(TrackingEvents.STARTED_AUTHENTICATION, {
          source: AuthenticationSource.AccountCreation
        })
        setIsLoadingPwlessLogin(true)
        const result = await sendPwlessLoginCode()
        setIsLoadingPwlessLogin(false)

        if (result.success) {
          onOpenAuthModal()
          tracker.track(TrackingEvents.CLICKED_SEND_CODE, {
            source: AuthenticationSource.AccountCreation
          })
        } else {
          // Show error to user - they can uncheck and try again
          setCreateAccount(false)
          setAccountCreationErrorCode(result.errorCode ?? null)
          // Always use localized error messages, never pass through BFF messages
          setAccountCreationError(t('confirmation-code.error-sending-code'))
        }
      }
    },
    [onOpenAuthModal, sendPwlessLoginCode, setCreateAccount]
  )

  return (
    <div className='createAccountCheckbox'>
      <div className='checkboxContainer'>
        {isLoadingPwlessLogin ? (
          <div className='loadingWrapper' data-testid='customer-info-loader'>
            <RotatingLines
              width='20'
              visible
              strokeColor='#888888'
              strokeWidth='3'
            />
          </div>
        ) : (
          <ToggleInput
            type='checkbox'
            name='createToastAccount'
            id='createToastAccount'
            dataTestId='createToastAccount'
            ariaLabelledBy='createAccountLabel'
            ariaDescribedBy='createAccountCheckbox-input-message'
            disabled={
              !phoneNumber || Object.keys(errors).includes('yourInfoPhone')
            }
            checked={createAccount}
            onChange={async (event: React.ChangeEvent<HTMLInputElement>) =>
              await handleCreateAccountCheckboxChange(event.target.checked)
            }
            onKeyDown={async (event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                event.stopPropagation()
                await handleCreateAccountCheckboxChange(!createAccount)
              }
            }}
          />
        )}
        <div className='createAccountLabel' id='createAccountLabel'>
          <span>
            {t(
              'customer-info-section.create-toast-account-for-quicker-checkout'
            )}
          </span>
          <span className='imageContainer'>
            <Image
              alt={t('customer-info-section.toast-logo')}
              aria-hidden='true'
              src='icons/toast-logo-filled.svg'
            />
          </span>
        </div>
      </div>
      <div
        className='inputMessage'
        id='createAccountCheckbox-input-message'
        data-testid='createAccountCheckbox-input-message'
      >
        {accountCreationError && (
          <div className='errorMessage' role='alert'>
            {accountCreationErrorCode ===
            PasswordlessAuthenticationErrorCode.DeactivatedPhone ? (
              <Trans
                i18nKey='confirmation-code.deactivated-phone-error'
                components={{
                  GuestHelpLink: (
                    <a
                      href={GUEST_HELP_URL}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {/* content */}
                    </a>
                  )
                }}
              />
            ) : (
              accountCreationError
            )}
          </div>
        )}
      </div>
      <div className='note' role='contentinfo'>
        <Trans
          i18nKey='customer-info-section.verification-code-permission-notice'
          components={{
            ToastTOSAndPrivacy: <ToastTOSAndPrivacy />,
            CAInfo: !isIntlRestaurant ? <CAInfo /> : <></>
          }}
        />
      </div>
      <Modal isOpen={isAuthModalOpen} onClose={onCloseAuthModalCb}>
        <ModalOverlay fadeIn fadeOut />
        <PwlessAuth
          source={AuthenticationSource.AccountCreation}
          currentLoginStage={LoginStage.EnterVerificationCode}
          showBackButton={false}
          accountInfo={{ phoneNumber, email, firstName, lastName }}
          onSuccessfulLogin={() => window.scrollTo(0, 0)}
        />
      </Modal>
    </div>
  )
}

export default CreateAccountCheckbox
